/**
 * SettingsShell — the per-route settings layout: page-head + rail + pane.
 *
 * Mirrors `prototype/settings/settings-company.html ▸ .set-layout`:
 *
 *   ┌──────────────────────────────────────────────────────────────────┐
 *   │  Page head — h1 + lead text                                      │
 *   ├──────────────┬───────────────────────────────────────────────────┤
 *   │  Settings    │  <children>                                       │
 *   │  rail        │  (form cards + save-bar)                          │
 *   │  (sticky)    │                                                   │
 *   └──────────────┴───────────────────────────────────────────────────┘
 *
 * Lives INSIDE WorkspaceShell — every settings route renders
 * `<WorkspaceShell breadcrumbs={…}><SettingsShell …>{form}</SettingsShell></WorkspaceShell>`.
 * Two shells stacked is intentional: the workspace shell handles the org
 * sidebar + topbar; this one handles the settings-specific second nav.
 *
 * The rail collapses to stacked above the pane on screens <lg.
 */
import { type ReactNode } from "react";
import { SettingsRail } from "./settings-rail.tsx";
import type { SectionId } from "./lib/sections.ts";

export interface SettingsShellProps {
  /** Which rail item to highlight + which section the page is rendering. */
  activeSection: SectionId;
  /** Page H1 — appears above the rail + pane grid. */
  pageTitle: string;
  /** Lead paragraph under the title. Optional. */
  pageDescription?: ReactNode;
  /** The form / content for the active section. Sits to the right of the rail. */
  children: ReactNode;
}

export function SettingsShell({
  activeSection,
  pageTitle,
  pageDescription,
  children,
}: SettingsShellProps) {
  return (
    <div className="flex flex-col gap-5">
      <header className="flex flex-col gap-1.5 max-w-[860px]">
        <h1 className="text-2xl font-bold text-ink leading-tight tracking-tight">{pageTitle}</h1>
        {pageDescription ? (
          <p className="text-sm text-muted leading-relaxed">{pageDescription}</p>
        ) : null}
      </header>

      {/* The 2-column grid. Rail = 240px fixed; pane = remainder. Stacks
          on screens narrower than lg. `min-w-0` on the pane lets long
          inputs / nested grids truncate cleanly inside the column. */}
      <div className="grid grid-cols-1 lg:grid-cols-[240px_minmax(0,1fr)] gap-5">
        <SettingsRail active={activeSection} />
        <div className="min-w-0 flex flex-col gap-4">{children}</div>
      </div>
    </div>
  );
}
